import { styled } from '@mui/material/styles';
import type {
  DataGridProProps,
  GridColumnResizeParams,
} from '@mui/x-data-grid-pro';
import { GRID_TREE_DATA_GROUPING_FIELD } from '@mui/x-data-grid-pro';
import { useCallback } from 'react';
import { useTreeDataGridRowsExpansionRestoreProps } from '@/features/DataGrid';
import { Analytics } from '@/shared/lib';
import type { ColumnsConfig } from '@/shared/types';
import type { MinMaxData } from '@/shared/ui';
import { getColumnHeaderHeight, DataGrid as SharedDataGrid } from '@/shared/ui';
import { useData } from './useData';
import { useTreeDataGridProps } from './useTreeDataGridProps';

const INITIAL_STATE: DataGridProProps['initialState'] = {
  pinnedColumns: {
    left: [GRID_TREE_DATA_GROUPING_FIELD],
  },
};

const getRowClassName = (): DataGridProProps['getRowClassName'] => (params) => {
  const classes = [];

  if (params.row.isTotal) {
    classes.push('total');
  }

  if (params.row.firstInGroup) {
    classes.push('firstRowInGroup');
  }
  if (params.row.lastInGroup) {
    classes.push('lastRowInGroup');
  }

  return classes.join(' ');
};

const DataGrid = styled(SharedDataGrid)(() => ({
  '& .MuiDataGrid-treeDataGroupingCell': {
    width: 'unset',

    '& > span': {
      minWidth: 0,
    },
  },

  '& .denseRow .MuiDataGrid-treeDataGroupingCellToggle': {
    marginRight: 8,

    '& button': {
      padding: 1,
    },
  },

  '& .groupingCell, & .groupingHeader': {
    borderRight: '1px solid #e0e0e0',
  },

  '& .total+.firstRowInGroup .MuiDataGrid-cell': {
    borderTop: '1px solid var(--rowBorderColor)',
  },

  '& .firstRowInGroup .MuiDataGrid-cell': {
    borderTop: '1px solid #9e9e9e',
  },

  '& .lastRowInGroup+.firstRowInGroup .MuiDataGrid-cell': {
    borderTop: 'none',
  },

  '& .lastRowInGroup+.MuiDataGrid-row .MuiDataGrid-cell': {
    borderTop: 'none',
  },

  '& .lastRowInGroup .MuiDataGrid-cell': {
    borderBottom: '1px solid #9e9e9e',
  },
}));

interface Props {
  isReadonly?: boolean;
  rowHeight: number;
  columnsWidth: Record<string, number | undefined>;
  columnsConfig: ColumnsConfig;
  dimensions: string[];
  metric: string;
  minMax?: MinMaxData[string];
  updateColumnsWidth?: (columnName: string, newWidth?: number) => void;
}

export function TreeDataGrid({
  isReadonly,
  rowHeight,
  columnsWidth,
  columnsConfig,
  dimensions,
  metric,
  minMax,
  updateColumnsWidth,
}: Props) {
  const { dataSource, dataColumns } = useData({
    dimensions,
  });
  const { columns, groupingColDef, getTreeDataPath } = useTreeDataGridProps({
    columnsConfig,
    columnsWidth,
    dimensions,
    metric,
    minMax,
    dataColumns,
    isReadonly,
  });
  const { isGroupExpandedByDefault } =
    useTreeDataGridRowsExpansionRestoreProps();

  const handleColumnWidthChange = useCallback(
    (params: GridColumnResizeParams) => {
      const { width, field } = params.colDef;

      updateColumnsWidth?.(field, width);
      Analytics.sendFeatureUsage('tree_data_grid', 'resize_column');
    },
    [updateColumnsWidth],
  );

  return (
    <DataGrid
      treeData
      initialState={INITIAL_STATE}
      disableColumnResize={isReadonly}
      columnHeaderHeight={getColumnHeaderHeight(rowHeight)}
      rowHeight={rowHeight}
      isGroupExpandedByDefault={isGroupExpandedByDefault}
      getRowClassName={getRowClassName()}
      columns={columns}
      groupingColDef={groupingColDef}
      getTreeDataPath={getTreeDataPath}
      unstable_dataSource={dataSource}
      onColumnWidthChange={handleColumnWidthChange}
    />
  );
}
